import {
  applyAllowStudiedAnswer,
  Progress,
  toPercent,
} from "../../../domains/DrillProgress";
import { LazyPopover, PopoverProvider } from "../../general/Popover/Popover";

type Props = {
  totalCount: number;
  progress: Progress;
  bold?: boolean;
  allowStudiedAnswer?: boolean;
};

export const DrillProgressBarGroup = ({
  totalCount,
  progress,
  bold = false,
  allowStudiedAnswer = true,
}: Props) => {
  const effectiveProgress = applyAllowStudiedAnswer(
    progress,
    allowStudiedAnswer,
  );
  return (
    <>
      <DrillProgressBar
        totalCount={totalCount}
        progressKey="perfectCount"
        progress={effectiveProgress}
        bold={bold}
      />
      <DrillProgressBar
        totalCount={totalCount}
        progressKey="establishedCount"
        progress={effectiveProgress}
        bold={bold}
      />
      <DrillProgressBar
        totalCount={totalCount}
        progressKey="studyingCount"
        progress={effectiveProgress}
        bold={bold}
      />
      <DrillProgressBar
        totalCount={totalCount}
        progressKey="studiedCount"
        progress={effectiveProgress}
        bold={bold}
      />
      <DrillProgressBar
        totalCount={totalCount}
        progressKey="notStudiedCount"
        progress={effectiveProgress}
        bold={bold}
      />
    </>
  );
};

type DrillProgressBarProps = {
  progress: Progress;
  progressKey: keyof Progress;
  totalCount: number;
  bold?: boolean;
};
const barColorClassNames = {
  perfectCount: "bg-blue-400",
  establishedCount: "bg-yellow-400",
  studyingCount: "bg-darkred-300",
  studiedCount: "bg-green-300",
  notStudiedCount: "bg-gray-400",
};
const locales = {
  perfectCount: "S",
  establishedCount: "A",
  studyingCount: "B",
  studiedCount: "完了",
  notStudiedCount: "未学習",
};

const DrillProgressBar = ({
  progressKey,
  progress,
  totalCount,
  bold = false,
}: DrillProgressBarProps) => {
  const colorClassName = barColorClassNames[progressKey];
  const numerator = numeratorWithFallback(progress, progressKey);
  const percent = toPercent(numerator, totalCount);

  return (
    // バーは 1 記録あたり 5 本あり一覧で大量に並ぶため、初めて開くまで Popover をマウントしない
    <PopoverProvider>
      <LazyPopover
        style={{ flexGrow: numerator, flexBasis: 0 }}
        className={`min-w-0 cursor-pointer p-0 ${colorClassName} ${bold ? "h-[10px]" : "h-[5px]"}`}
        content={`${locales[progressKey]}：${percent}`}
      />
    </PopoverProvider>
  );
};

// 定着度を持たない場合は未学習に1を設定することで進捗度バーを表示
const numeratorWithFallback = (
  progress: Progress,
  progressKey: keyof Progress,
) => {
  const hasNoRetentionLevel = !Object.values(progress).find((i) => i && i > 0);
  if (hasNoRetentionLevel && progressKey === "notStudiedCount") {
    return 1;
  }

  return progress[progressKey] ?? 0;
};
